import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { EnhancedToolResult, ImageContent, TextContent, ToolExecutionContext } from '../types';
import { detectShots } from '../video/detectShots';
import { encodeFrame } from '../video/encodeFrame';
import { VideoDecoder } from '../video/videoDecoder';
import type { ClipShot, KeptRegion } from '../video/clipFraming';
import {
    angleChangeTimesMs,
    assignCameraAngles,
    buildClipShots,
    faceCheckTimesMs,
    formatShotTable,
    keptDurationMs,
    middleFaceCheck,
    planClipSampleTimesMs,
    sortFacesLeftToRight,
} from '../video/clipFraming';
import { rememberClipFraming } from '../video/clipFramingStore';
import {
    cellHeightForFrame,
    compositeStrip,
    groupShotsIntoStrips,
    perStripForCellHeight,
    TILE_WIDTH,
} from '../video/video-tile-strip';

import { isAnalyzableMediaUrl } from './AnalyzeVideoTool';
import { resolveSceneGroupById, sceneGroupDisplayId } from './scene-group-addressing';
import { Tool } from './Tool';
import { getInterleavedTranscript } from './transcript-tokens';

import type { BoundingBox } from 'js/src/client';
import { MaxVideoDurationSeconds } from 'js/src/client';
import { detectFacesInRecording } from 'js/src/luminary';
import {
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isTalkingHeadSceneGroup,
} from 'js/src/luminary/luminary-selectors';

export const analyzeClipFramingSchema = z.object({
    scene_group_id: z
        .string()
        .describe(
            'The stable scene_group_id of the talking-head scene group holding the clip (from the video state).',
        ),
});

export type AnalyzeClipFramingArgs = z.infer<typeof analyzeClipFramingSchema>;

/** Shot detection only needs a thumbnail: its hash is a 9x8 grid. */
const HASH_FRAME_WIDTH_PX = 160;
const FACE_DETECTION_CONCURRENCY = 6;
const BOX_COLOR = '#00e676';

function downscale(source: HTMLCanvasElement, widthPx: number): HTMLCanvasElement {
    const target = document.createElement('canvas');
    target.width = widthPx;
    target.height = Math.max(1, Math.round((source.height * widthPx) / source.width));
    target.getContext('2d')?.drawImage(source, 0, 0, target.width, target.height);
    return target;
}

async function runWithConcurrency<T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]> {
    const results: T[] = new Array(tasks.length);
    let next = 0;
    const worker = async () => {
        while (next < tasks.length) {
            const index = next++;
            results[index] = await tasks[index]();
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
    return results;
}

/** One contact-sheet panel: the shot's frame with its number and every face boxed and numbered. */
function drawContactPanel(
    frame: HTMLCanvasElement,
    shotNumber: number,
    faces: BoundingBox[],
    heightPx: number,
): HTMLCanvasElement {
    const panel = document.createElement('canvas');
    panel.width = TILE_WIDTH;
    panel.height = heightPx;
    const ctx = panel.getContext('2d');
    if (!ctx) {
        throw new Error('Could not acquire 2D context for the contact sheet');
    }
    ctx.drawImage(frame, 0, 0, panel.width, panel.height);
    ctx.font = 'bold 22px sans-serif';
    ctx.textBaseline = 'top';
    ctx.lineWidth = 3;
    faces.forEach((face, index) => {
        const x = face.x * panel.width;
        const y = face.y * panel.height;
        ctx.strokeStyle = BOX_COLOR;
        ctx.strokeRect(x, y, face.width * panel.width, face.height * panel.height);
        ctx.fillStyle = BOX_COLOR;
        ctx.fillRect(x, Math.max(0, y - 26), 24, 26);
        ctx.fillStyle = '#000';
        ctx.fillText(String(index + 1), x + 6, Math.max(0, y - 24));
    });
    const label = `Shot ${shotNumber}`;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.fillRect(0, 0, ctx.measureText(label).width + 16, 30);
    ctx.fillStyle = '#fff';
    ctx.fillText(label, 8, 4);
    return panel;
}

/**
 * Builds the shot table and contact sheet for a clip: the camera shots inside the part of the
 * recording a talking-head scene group plays, the words each shot plays under, and the faces in
 * each. Frames are decoded in the browser and faces are found by the face detection service, so
 * this only runs on a clip, never on a whole long recording.
 */
export class AnalyzeClipFramingTool extends Tool<typeof analyzeClipFramingSchema> {
    protected override readonly schema = analyzeClipFramingSchema;
    protected override readonly toolName = TOOL_NAMES.analyzeClipFraming;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription = `Look closely at a clip before reframing it. Takes the talking-head scene group holding the clip (not a whole recording) and returns its shot table, one row per camera shot inside the clip with its time, the exact transcript words it plays under, and the faces in it, numbered left to right as ${TOOL_NAMES.editMedia}'s frame_speaker numbers them, plus a contact sheet showing each shot's frame with the faces boxed and numbered. Use it to plan which scenes to add and how to frame each one.`;

    public getToolDisplayName(): string {
        return 'Analyzing clip framing';
    }

    public get statusIcon(): string {
        return 'crop';
    }

    public async execute(
        args: AnalyzeClipFramingArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const resolved = resolveSceneGroupById(this.luminary, args.scene_group_id);
        if ('error' in resolved) {
            return { result: `Error: ${resolved.error}`, isSuccess: false };
        }
        const { sceneGroup } = resolved;
        const sceneGroupShortId = sceneGroupDisplayId(this.luminary, sceneGroup.id);
        const recording = isTalkingHeadSceneGroup(sceneGroup)
            ? sceneGroup.mediaAsset?.sourceData
            : null;
        if (!recording?.uuid) {
            return {
                result: `Error: scene group ${sceneGroupShortId} isn't a talking-head scene group with a recording. Reframing works on a clip cut from a recording with ${TOOL_NAMES.addTalkingHeadSceneGroup}.`,
                isSuccess: false,
            };
        }
        const sourceUrl = recording.preview_url ?? recording.render_url;
        if (!sourceUrl || !isAnalyzableMediaUrl(sourceUrl)) {
            return {
                result: `Error: the recording in scene group ${sceneGroupShortId} can't be read for analysis.`,
                isSuccess: false,
            };
        }

        const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const regions: KeptRegion[] =
            playbackRegions.length > 0
                ? playbackRegions.map(([start, end]) => [start, end] as KeptRegion)
                : [[0, getVoiceDrivenBaseAssetSourceDuration(sceneGroup)]];
        const maxClipMs = MaxVideoDurationSeconds.LIMIT * 1000;
        const clipMs = keptDurationMs(regions);
        if (clipMs > maxClipMs) {
            return {
                result: `Error: scene group ${sceneGroupShortId} plays ${Math.ceil(clipMs / 60000)} minutes of the recording. Reframing works on a clip of at most ${MaxVideoDurationSeconds.LIMIT / 60} minutes: cut the clip first with ${TOOL_NAMES.addTalkingHeadSceneGroup} and its start_quote and end_quote.`,
                isSuccess: false,
            };
        }

        const decoder = new VideoDecoder();
        try {
            const meta = await decoder.loadUrl(sourceUrl);
            const samples = [];
            for (const timeMs of planClipSampleTimesMs(regions)) {
                const frame = await decoder.extractFrameAt(timeMs / 1000);
                samples.push({
                    timeS: timeMs / 1000,
                    canvas: downscale(frame, HASH_FRAME_WIDTH_PX),
                });
            }
            const { cutTimestampsS } = detectShots(samples);
            const cutTimesMs = cutTimestampsS.map((timeS) => Math.round(timeS * 1000));
            const shots = buildClipShots(
                cutTimesMs,
                regions,
                getInterleavedTranscript(this.getState(), sceneGroup),
            );

            const recordingUuid = recording.uuid;
            const faceChecks = shots.flatMap((shot) =>
                faceCheckTimesMs(shot).map((timeMs) => ({ shot, timeMs })),
            );
            const detections = await runWithConcurrency(
                faceChecks.map(({ timeMs }) => async () => {
                    try {
                        return sortFacesLeftToRight(
                            await detectFacesInRecording(recordingUuid, timeMs),
                        );
                    } catch {
                        return null;
                    }
                }),
                FACE_DETECTION_CONCURRENCY,
            );
            faceChecks.forEach(({ shot, timeMs }, index) => {
                shot.faceChecks.push({ timeMs, faces: detections[index] });
            });
            assignCameraAngles(shots);

            rememberClipFraming(sceneGroup.id, {
                recordingUuid,
                angleChangeTimesMs: angleChangeTimesMs(shots),
                shots: shots.map(({ number, startMs, endMs, angle, isBroll }) => ({
                    number,
                    startMs,
                    endMs,
                    angle,
                    isBroll,
                })),
            });

            const resultContent: (TextContent | ImageContent)[] = [
                { type: 'text', text: formatShotTable(shots, regions) },
                ...(await this.buildContactSheet(decoder, meta, shots)),
            ];
            const angles = new Set(shots.filter((shot) => !shot.isBroll).map((shot) => shot.angle))
                .size;
            const broll = shots.filter((shot) => shot.isBroll).length;
            return {
                result: `Analyzed the clip in scene group ${sceneGroupShortId}: ${shots.length} ${shots.length === 1 ? 'shot' : 'shots'}, ${angles} camera ${angles === 1 ? 'angle' : 'angles'} of people and ${broll} b-roll ${broll === 1 ? 'shot' : 'shots'}.`,
                resultContent,
                isSuccess: true,
            };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'unknown error';
            return {
                result: `Error: could not analyze the clip in scene group ${sceneGroupShortId}: ${message}`,
                isSuccess: false,
            };
        } finally {
            decoder.dispose();
        }
    }

    private async buildContactSheet(
        decoder: VideoDecoder,
        meta: { widthPx: number; heightPx: number },
        shots: ClipShot[],
    ): Promise<(TextContent | ImageContent)[]> {
        const cellHeightPx = cellHeightForFrame(meta.widthPx, meta.heightPx);
        const panels: string[] = [];
        for (const shot of shots) {
            const check = middleFaceCheck(shot);
            const timeMs = check?.timeMs ?? (shot.startMs + shot.endMs) / 2;
            const frame = await decoder.extractFrameAt(timeMs / 1000);
            const panel = drawContactPanel(frame, shot.number, check?.faces ?? [], cellHeightPx);
            panels.push(encodeFrame(panel, TILE_WIDTH));
        }
        const groups = groupShotsIntoStrips(panels.length, perStripForCellHeight(cellHeightPx));
        const parts: (TextContent | ImageContent)[] = [];
        for (let g = 0; g < groups.length; g++) {
            const shotNumbers = groups[g].map((index) => shots[index].number);
            parts.push({
                type: 'text',
                text: `Contact sheet ${g + 1}/${groups.length}: shots ${shotNumbers.join(', ')}, left to right.`,
            });
            const strip = await compositeStrip(
                groups[g].map((index) => panels[index]),
                TILE_WIDTH,
                cellHeightPx,
            );
            parts.push({ type: 'image_url', image_url: { url: strip } });
        }
        return parts;
    }
}
